"use client";

import {
  CheckCircle2,
  CircleHelp,
  Info,
  LoaderCircle,
  WandSparkles,
} from "lucide-react";
import Image from "next/image";
import { useMemo, useState } from "react";
import { toast as sonnerToast } from "sonner";
import {
  defaultFacelessMotionVideoModelId,
  defaultFacelessMotionVideoResolution,
  facelessImageStylePresets,
  getFacelessScriptSanityMessage,
  getFacelessMotionVideoModelOption,
  isFacelessMotionVideoModelId,
  type FacelessMotionVideoModelId,
  type FacelessMotionVideoResolution,
  type SceneVisualMode,
} from "@reelmagic/domain";
import { Button } from "@/components/ui/button";
import { CreditAmount } from "@/components/ui/credit-amount";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SceneDensitySlider } from "@/components/ui/scene-density-slider";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { Slider } from "@/components/ui/slider";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Textarea } from "@/components/ui/textarea";
import { playbackSpeedLimits } from "@reelmagic/remotion";
import { formatDurationFromMs } from "../../lib/format-duration";
import { getFacelessLiveQuote } from "../../lib/generation/faceless";
import {
  createFieldLabelClassName,
  createFieldLabelTextClassName,
  createQuoteAmountClassName,
  createQuoteClassName,
  createSplitFieldsClassName,
  createSupportingTextClassName,
} from "./CreatePageShell";
import {
  defaultFacelessGeneratorDraft,
  parseFacelessGeneratorDraft,
  type FacelessGeneratorDraft,
} from "./faceless-generator-draft";
import {
  TtsModelSelectField,
  VoiceSelectField,
} from "./VoiceSelectField";
import { FacelessScriptGenerationModal } from "./FacelessScriptGenerationModal";
import { VideoModelPicker } from "./VideoModelPicker";
import { useTemplateCreationSettings } from "./use-template-creation-settings";
import { resolveFacelessCreationSettings } from "./template-creation-settings";
import { useFirebaseAuth } from "../auth/AuthProvider";

const visualModeOptions = [
  { description: "Still scenes", label: "Image Slideshow", value: "image" },
  { description: "AI video scenes", label: "Video Reel", value: "motion" },
] as const satisfies ReadonlyArray<{
  description: string;
  label: string;
  value: SceneVisualMode;
}>;

const facelessFormSectionClassName = "grid gap-4";
const facelessFooterClassName =
  "mt-1 flex items-end justify-between gap-4 rounded-xl border border-white/[0.1] bg-white/[0.025] p-3 max-[720px]:flex-col max-[720px]:items-center max-[720px]:text-center";

function resolveSupportedMotionVideoResolution(
  modelId: FacelessMotionVideoModelId,
  resolution: FacelessMotionVideoResolution,
) {
  const model = getFacelessMotionVideoModelOption(modelId);

  if (model?.supportedResolutions.includes(resolution)) {
    return resolution;
  }

  if (
    model?.supportedResolutions.includes(defaultFacelessMotionVideoResolution)
  ) {
    return defaultFacelessMotionVideoResolution;
  }

  return model?.supportedResolutions[0] ?? defaultFacelessMotionVideoResolution;
}

type FacelessGeneratorFormProps = {
  initialDraft?: FacelessGeneratorDraft | null;
  isSubmitting?: boolean;
  onScriptGenerationRequest?: (draft: FacelessGeneratorDraft) => void;
  onSubmit: (draft: FacelessGeneratorDraft) => void;
  submitLabel: string;
  submittingLabel?: string;
};

export function FacelessGeneratorForm({
  initialDraft,
  isSubmitting = false,
  onScriptGenerationRequest,
  onSubmit,
  submitLabel,
  submittingLabel = "Starting generation...",
}: FacelessGeneratorFormProps) {
  const { user } = useFirebaseAuth();
  const resolvedInitialDraft =
    parseFacelessGeneratorDraft(initialDraft) ?? defaultFacelessGeneratorDraft;
  const [title, setTitle] = useState(resolvedInitialDraft.title);
  const [script, setScript] = useState(resolvedInitialDraft.script);
  const [scriptDescription, setScriptDescription] = useState(
    resolvedInitialDraft.scriptDescription,
  );
  const [isScriptGenerationModalOpen, setIsScriptGenerationModalOpen] =
    useState(resolvedInitialDraft.openScriptGenerationModal);
  const {
    isSettingsLoading,
    setSettings,
    settings: {
      imageStylePresetId,
      motionVideoModelId,
      motionVideoResolution,
      musicTrackId,
      playbackSpeed,
      sceneDensity,
      scriptTargetDurationSeconds: targetDurationSeconds,
      ttsModelId,
      visualMode,
      voiceId,
    },
  } = useTemplateCreationSettings({
    initialSettings: resolveFacelessCreationSettings({
      imageStylePresetId: resolvedInitialDraft.imageStylePresetId,
      motionVideoModelId: resolvedInitialDraft.motionVideoModelId,
      motionVideoResolution: resolvedInitialDraft.motionVideoResolution,
      musicTrackId: resolvedInitialDraft.musicTrackId,
      playbackSpeed: resolvedInitialDraft.playbackSpeed,
      sceneDensity: resolvedInitialDraft.sceneDensity,
      scriptTargetDurationSeconds: resolvedInitialDraft.targetDurationSeconds,
      ttsModelId: resolvedInitialDraft.ttsModelId,
      visualMode: resolvedInitialDraft.visualMode,
      voiceId: resolvedInitialDraft.voiceId,
    }),
    loadSavedSettings: !initialDraft,
    resolveSavedSettings: resolveFacelessCreationSettings,
    templateType: "faceless_video",
  });

  const selectedMotionVideoModel =
    getFacelessMotionVideoModelOption(motionVideoModelId) ??
    getFacelessMotionVideoModelOption(defaultFacelessMotionVideoModelId);
  const motionVideoResolutionOptions = (
    selectedMotionVideoModel?.supportedResolutions ?? [
      defaultFacelessMotionVideoResolution,
    ]
  ).map((resolution) => ({
    description: resolution === "720p" ? "Lower cost" : "Sharper",
    label: resolution,
    value: resolution,
  }));

  const quote = useMemo(
    () =>
      getFacelessLiveQuote({
        motionVideoModelId,
        motionVideoResolution,
        playbackSpeed,
        sceneDensity,
        script,
        ttsModelId,
        visualMode,
      }),
    [
      motionVideoModelId,
      motionVideoResolution,
      playbackSpeed,
      sceneDensity,
      script,
      ttsModelId,
      visualMode,
    ],
  );

  function updateMotionVideoModelId(value: string) {
    if (!isFacelessMotionVideoModelId(value)) {
      return;
    }

    setSettings((current) => ({
      ...current,
      motionVideoModelId: value,
      motionVideoResolution: resolveSupportedMotionVideoResolution(
        value,
        current.motionVideoResolution,
      ),
    }));
  }

  function getCurrentDraft({
    openScriptGenerationModal = false,
    script: draftScript = script,
  }: Partial<
    Pick<FacelessGeneratorDraft, "openScriptGenerationModal" | "script">
  > = {}): FacelessGeneratorDraft {
    return {
      imageStylePresetId,
      motionVideoModelId,
      motionVideoResolution,
      musicTrackId,
      openScriptGenerationModal,
      playbackSpeed,
      sceneDensity,
      script: draftScript,
      scriptDescription,
      targetDurationSeconds,
      title: title.trim(),
      ttsModelId,
      visualMode,
      voiceId: voiceId.trim(),
    };
  }

  function openScriptGenerationModal() {
    if (onScriptGenerationRequest) {
      onScriptGenerationRequest(
        getCurrentDraft({ openScriptGenerationModal: true }),
      );
      return;
    }

    if (!user) {
      sonnerToast.error("Sign in is required before generating a script.");
      return;
    }

    setIsScriptGenerationModalOpen(true);
  }

  function submitRequest() {
    if (isSubmitting || isSettingsLoading) {
      return;
    }

    const trimmedScript = script.trim();

    if (!trimmedScript) {
      sonnerToast.error("Provide a voiceover script.");
      return;
    }

    const scriptSanityMessage = getFacelessScriptSanityMessage(trimmedScript);

    if (scriptSanityMessage) {
      sonnerToast.error(scriptSanityMessage);
      return;
    }

    onSubmit({
      ...getCurrentDraft({ script: trimmedScript }),
      openScriptGenerationModal: false,
    });
  }

  const estimatedDurationAtSpeedMs = Math.round(
    quote.estimatedDurationMs / playbackSpeed,
  );
  const playbackSpeedLabel = `${playbackSpeed.toFixed(1).replace(/\.0$/, "")}x`;

  // Keep a generous rhythm now that fields flow on one shared surface.
  return (
    <div className="@container grid w-full min-w-0 gap-6">
      <div className={facelessFormSectionClassName}>
        <Label className={createFieldLabelClassName}>
          <span className={createFieldLabelTextClassName}>Project Title</span>
          <Input
            maxLength={120}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="E.g. The Mystery of the Deep Ocean"
            type="text"
            value={title}
          />
        </Label>
      </div>

      <div className={facelessFormSectionClassName}>
        <div className={createFieldLabelClassName}>
          <div className="flex items-center justify-between gap-3">
            <Label
              className={createFieldLabelTextClassName}
              htmlFor="faceless-voiceover-script"
            >
              Voiceover Script
            </Label>
            <button
              className="inline-flex shrink-0 items-center gap-1.5 text-[0.86rem] font-semibold text-primary underline underline-offset-2 transition-colors hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              onClick={openScriptGenerationModal}
              type="button"
            >
              <WandSparkles aria-hidden="true" className="size-3.5" />
              Generate Script
            </button>
          </div>
          <Textarea
            id="faceless-voiceover-script"
            onChange={(event) => setScript(event.target.value)}
            placeholder="Describe the story or topic you want to generate a video about..."
            rows={7}
            value={script}
          />
          <div className="flex items-start gap-2 rounded-md border border-white/[0.065] bg-white/[0.03] px-3 py-2 text-[0.78rem] leading-5 text-[var(--rm-text-soft)]">
            <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
            <p className="m-0">
              Provide only single-speaker dialogue. Do not include any
              non-dialogue text, such as section headings or scene cues.
            </p>
          </div>
        </div>
      </div>

      <FacelessScriptGenerationModal
        defaultDescription={scriptDescription}
        defaultTargetDurationSeconds={targetDurationSeconds}
        isOpen={isScriptGenerationModalOpen}
        onClose={() => setIsScriptGenerationModalOpen(false)}
        onScriptGenerated={({
          description,
          script: generatedScript,
          targetDurationSeconds: generatedTargetDurationSeconds,
        }) => {
          setScript(generatedScript);
          setScriptDescription(description);
          setSettings((current) => ({
            ...current,
            scriptTargetDurationSeconds: generatedTargetDurationSeconds,
          }));
        }}
      />

      <div className={facelessFormSectionClassName}>
        <div className={createSplitFieldsClassName}>
          <TtsModelSelectField
            inputCharacters={script.length}
            label="Audio model"
            onValueChange={(selection) => {
              setSettings((current) => ({
                ...current,
                ttsModelId: selection.ttsModelId,
                voiceId: selection.voiceId,
              }));
            }}
            value={ttsModelId}
          />

          <VoiceSelectField
            label="Voice Narration"
            onValueChange={(selection) => {
              setSettings((current) => ({
                ...current,
                ttsModelId: selection.ttsModelId,
                voiceId: selection.voiceId,
              }));
            }}
            ttsModelId={ttsModelId}
            voiceId={voiceId}
          />

          <Label className="grid gap-3 py-3">
            <span
              className={`${createFieldLabelTextClassName} inline-flex items-center gap-1.5`}
            >
              <span>Speed: {playbackSpeedLabel}</span>
              <TooltipProvider delayDuration={120}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      aria-label="Narration playback speed info"
                      className="inline-flex size-4 items-center justify-center rounded-full text-[var(--rm-text-muted)] transition-colors hover:text-[var(--rm-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                      type="button"
                    >
                      <CircleHelp aria-hidden="true" className="size-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[220px] text-[0.78rem] leading-5">
                    Narration playback speed can be changed later
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </span>
            <Slider
              aria-label="Playback speed"
              max={playbackSpeedLimits.max}
              min={playbackSpeedLimits.min}
              onValueChange={([value]) =>
                setSettings((current) => ({
                  ...current,
                  playbackSpeed:
                    Math.round(
                      (value ?? playbackSpeedLimits.default) * 10,
                    ) / 10,
                }))
              }
              step={0.1}
              value={[playbackSpeed]}
            />
          </Label>
        </div>

        <div className="grid gap-4">
          <div className={createFieldLabelClassName}>
            <span className={createFieldLabelTextClassName}>Visual Type</span>
            <SegmentedControl
              ariaLabel="Visual type"
              onValueChange={(value) =>
                setSettings((current) => ({
                  ...current,
                  visualMode: value,
                }))
              }
              options={visualModeOptions}
              value={visualMode}
            />
          </div>

          {visualMode === "motion" ? (
            <div className="flex flex-wrap items-center gap-3">
              <Label
                className={`${createFieldLabelClassName} w-full min-w-[260px] @min-[720px]:w-[360px] @min-[1040px]:w-[420px]`}
              >
                <span className={createFieldLabelTextClassName}>
                  Video Model
                </span>
                <div className="mt-2">
                  <VideoModelPicker
                    onValueChange={updateMotionVideoModelId}
                    resolution={motionVideoResolution}
                    value={motionVideoModelId}
                  />
                </div>
              </Label>

              <div className={`${createFieldLabelClassName} shrink-0`}>
                <span className={createFieldLabelTextClassName}>
                  Scene Resolution
                </span>
                <SegmentedControl
                  ariaLabel="Video scene resolution"
                  onValueChange={(value) =>
                    setSettings((current) => ({
                      ...current,
                      motionVideoResolution: value,
                    }))
                  }
                  options={motionVideoResolutionOptions}
                  value={motionVideoResolution}
                />
              </div>
            </div>
          ) : null}

          <div className={createFieldLabelClassName}>
            <SceneDensitySlider
              ariaLabel="Scene pacing"
              labelClassName={createFieldLabelTextClassName}
              onValueChange={(value) =>
                setSettings((current) => ({
                  ...current,
                  sceneDensity: value,
                }))
              }
              value={sceneDensity}
            />
          </div>
        </div>
      </div>

      <div className={facelessFormSectionClassName}>
        <div className={createFieldLabelClassName}>
            <span className={createFieldLabelTextClassName}>Visual Style</span>
          <div className="grid grid-cols-5 gap-1.5">
            {facelessImageStylePresets.map((preset) => {
              const isSelected = preset.id === imageStylePresetId;

              return (
                <button
                  className={`overflow-hidden rounded-lg border text-left transition-colors ${
                    isSelected
                      ? "border-primary bg-primary/10 ring-2 ring-primary/70 ring-offset-1 ring-offset-background"
                      : "border-border/60 bg-background hover:border-primary/50"
                  }`}
                  key={preset.id}
                  onClick={() => {
                    setSettings((current) => ({
                      ...current,
                      imageStylePresetId: preset.id,
                    }));
                  }}
                  type="button"
                >
                  <div className="relative aspect-square w-full bg-muted/30">
                    <Image
                      alt={`${preset.label} style preview`}
                      className="object-cover"
                      fill
                      sizes="(max-width: 640px) 22vw, (max-width: 1024px) 14vw, 100px"
                      src={preset.previewImageSrc}
                    />
                    {isSelected ? (
                      <div className="absolute right-1 top-1 rounded-full bg-black/65 p-0.5 text-white">
                        <CheckCircle2 aria-hidden="true" className="size-3.5" />
                      </div>
                    ) : null}
                  </div>
                  <div className="p-1">
                    <div
                      className={`truncate text-[11px] font-medium leading-tight ${
                        isSelected ? "text-primary" : ""
                      }`}
                    >
                      {preset.label}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className={facelessFooterClassName}>
        <div className={createQuoteClassName}>
          <strong className={createQuoteAmountClassName}>
            <CreditAmount
              amount={quote.totalCredits}
              className="[&_svg]:size-[0.8em]"
            />
          </strong>
          <span className={createSupportingTextClassName}>
            Approx. {formatDurationFromMs(estimatedDurationAtSpeedMs)} at{" "}
            {playbackSpeedLabel} speed, {quote.estimatedSceneCount ?? 0} scenes
          </span>
        </div>

        <Button
          disabled={isSubmitting || isSettingsLoading}
          onClick={submitRequest}
          type="button"
          variant="aura"
        >
          {isSubmitting ? (
            <LoaderCircle aria-hidden="true" className="size-4 animate-spin" />
          ) : null}
          {isSubmitting ? submittingLabel : submitLabel}
        </Button>
      </div>
    </div>
  );
}
